/**
 * Field configuration for the two Pentomino calculators.
 * Field IDs mirror the engine's `calc-engine.js` DEFAULTS keys exactly.
 * Labels / helps / tags are pulled from i18n at runtime via `labelKey`.
 *
 * fmt:
 *   - "mxn"  → MXN currency amount, stored as number
 *   - "int"  → integer
 *   - "dec"  → decimal
 *   - "pct"  → UI shows percentage (0-100), engine receives fraction (0-1)
 */
import PentominoCalc from "./calc-engine";

const D = PentominoCalc.DEFAULTS;

export const FIELDS = {
    comercio: {
        primary: [
            { id: "sucursales", labelKey: "sucursales", fmt: "int", min: 1, max: 300, step: 1, v: 10 },
            { id: "efectivoMensualPorSucursal", labelKey: "efectivoMensualPorSucursal", fmt: "mxn", min: 200000, max: 12000000, step: 10000, v: 2340000 },
            { id: "recoleccionesSemana", labelKey: "recoleccionesSemana", fmt: "dec", min: 0.5, max: 7, step: 0.5, v: 3, helpKey: "recoleccionesSemanaHelp" },
            { id: "retirosPorCajero", labelKey: "retirosPorCajero", fmt: "int", min: 100, max: 3000, step: 10, v: D.retirosPorCajero, helpKey: "retirosPorCajeroHelp" },
        ],
        advanced: [
            { groupKey: "tarifasETV", fields: [
                { id: "tarifaServicio", labelKey: "tarifaServicio", fmt: "mxn", v: D.tarifaServicio, min: 100, max: 3000, step: 10 },
                { id: "cuotaRiesgoMillar", labelKey: "cuotaRiesgoMillar", fmt: "dec", v: D.cuotaRiesgoMillar, min: 0, max: 10, step: 0.05 },
                { id: "procesoMillar", labelKey: "procesoMillar", fmt: "dec", v: D.procesoMillar, min: 0, max: 10, step: 0.05 },
                { id: "minutosExcedentes", labelKey: "minutosExcedentes", fmt: "dec", v: 3, min: 0, max: 120, step: 1 },
            ]},
            { groupKey: "costosInternos", fields: [
                { id: "horasPorServicio", labelKey: "horasPorServicio", fmt: "dec", v: D.horasPorServicio, min: 0, max: 8, step: 0.25 },
                { id: "costoHora", labelKey: "costoHora", fmt: "mxn", v: D.costoHora, min: 50, max: 1000, step: 10 },
                { id: "diasTransito", labelKey: "diasTransito", fmt: "int", v: D.diasTransito, min: 0, max: 15, step: 1 },
                { id: "tasaOportunidad", labelKey: "tasaOportunidad", fmt: "pct", v: D.tasaOportunidad, min: 0, max: 30, step: 0.25 },
            ]},
        ],
    },
    sponsor: {
        primary: [
            { id: "cajeros", labelKey: "cajeros", fmt: "int", min: 5, max: 1000, step: 5, v: 60 },
            { id: "retirosPorCajero", labelKey: "retirosPorCajero", fmt: "int", min: 100, max: 3000, step: 10, v: D.retirosPorCajero, helpKey: "retirosPorCajeroHelp" },
            { id: "efectivoDepositadoPorCajero", labelKey: "efectivoDepositadoPorCajero", fmt: "mxn", min: 200000, max: 12000000, step: 10000, v: 2340000 },
            { id: "comisionSponsorRetiro", labelKey: "comisionSponsorRetiro", fmt: "dec", min: 0.5, max: 5, step: 0.05, v: Math.round(D.costoRetiro.sponsor * 100) / 100 },
        ],
        advanced: [
            { groupKey: "operacionYComisiones", fields: [
                { id: "comisionSponsorConsulta", labelKey: "comisionSponsorConsulta", fmt: "dec", v: Math.round(D.costoConsulta.sponsor * 100) / 100, min: 0, max: 5, step: 0.05 },
                { id: "pctConsultas", labelKey: "pctConsultas", fmt: "pct", v: D.pctConsultas, min: 0, max: 50, step: 0.1 },
                { id: "retiroPromedio", labelKey: "retiroPromedio", fmt: "mxn", v: Math.round(D.retiroPromedio), min: 500, max: 10000, step: 50 },
            ]},
            { groupKey: "fondeo", fields: [
                { id: "diasPermanenciaFondeo", labelKey: "diasPermanenciaFondeo", fmt: "int", v: D.diasPermanenciaFondeo, min: 1, max: 90, step: 1 },
                { id: "margenFondeoAnual", labelKey: "margenFondeoAnual", fmt: "pct", v: D.margenFondeoAnual, min: 0, max: 15, step: 0.25 },
            ]},
        ],
    },
};

export const toUI = (c) => (c.fmt === "pct" ? +(c.v * 100).toFixed(4) : c.v);

export const fromUI = (c, val) => {
    let n = parseFloat(val);
    if (isNaN(n)) n = 0;
    // clamp to min/max if present
    if (c.min != null && n < c.min) n = c.min;
    if (c.max != null && n > c.max) n = c.max;
    return c.fmt === "pct" ? n / 100 : n;
};

export const clampUI = (c, rawVal) => {
    let n = parseFloat(rawVal);
    if (isNaN(n)) return "";
    if (c.min != null && n < c.min) n = c.min;
    if (c.max != null && n > c.max) n = c.max;
    return n;
};
